/** Pure helpers for the customer-pane people strip (#616). */

export const VISIBLE_PANE_PEOPLE = 3

export interface PanePerson {
  email: string
  name?: string
  partyKind?: 'external' | 'contractor'
  partyRef?: string
  partyName?: string
  hasWorkOrder?: boolean
  /** Desk ticket contact. The strip shows this person first (#616). */
  ticketContact?: boolean
  /** Desk contact id, when this address has a contact page (#548). */
  contactId?: string
}

/** Name, or the mailbox local part when the person has no name. Never the raw wrapping address. */
export function panePersonLabel(person: Pick<PanePerson, 'name' | 'email'>): string {
  const name = person.name?.trim()
  if (name && name.toLowerCase() !== person.email.toLowerCase()) return name
  const local = person.email.split('@')[0]?.trim()
  return local || person.email
}

function isTicketContact(person: object): boolean {
  return 'ticketContact' in person && Boolean((person as { ticketContact?: boolean }).ticketContact)
}

/** Ticket contact first. Everyone else keeps the order they arrived in. */
export function orderPanePeople<T extends { email: string }>(people: readonly T[]): T[] {
  const index = people.findIndex((person) => isTicketContact(person))
  if (index <= 0) return [...people]
  const next = [...people]
  const [contact] = next.splice(index, 1)
  return contact ? [contact, ...next] : next
}

export function peopleCountLabel(count: number): string {
  return count === 1 ? '1 person' : `${count} people`
}

/** Fill contact ids from GET /threads/{id}/recipients without dropping one the case already knows. */
export function withContactIds<T extends { email: string; contactId?: string }>(
  people: readonly T[],
  options: readonly { email: string; contact_id?: string | null }[],
): Array<T & { contactId?: string }> {
  const ids = new Map<string, string>()
  for (const option of options) {
    const id = option.contact_id?.trim()
    if (id) ids.set(option.email.toLowerCase(), id)
  }
  if (!ids.size) return [...people]
  return people.map((person) => {
    if (person.contactId) return person
    const id = ids.get(person.email.toLowerCase())
    return id ? { ...person, contactId: id } : person
  })
}
